import React, { useContext, useRef, useState } from 'react';
import { noop } from 'lodash';
import { getIsMobile } from '../../../utils/getIsMobile';
import { withInvariant } from '../../../utils/withInvariant';
import { Box } from '../../layout/Box';
import { UserUI } from '../../users/types';
import { PostCardProps } from '../PostCard/PostCard';
import { useTrackPostVisible } from './hooks/useTrackPostVisible';
import { PostCarousel } from './parts/PostCarousel';
import { PostControls } from './parts/PostControls';
import { PostFooter } from './parts/PostFooter';
import { PostHeader } from './parts/PostHeader';
import { PostText } from './parts/PostText';
import { Root } from './styles';
import { getCarouselSizes } from './utils';

export interface PostDetailsCardContextPayload {
    soundControlContainer: HTMLDivElement | null;
    isProductExpanded: boolean;
    activeIndex: number;
    sizes: ReturnType<typeof getCarouselSizes>;
    setSoundControlContainer: (container: HTMLDivElement | null) => void;
    setIsProductExpanded: (expanded: boolean) => void;
    setActiveIndex: (index: number) => void;
    setSizes: (sizes: ReturnType<typeof getCarouselSizes>) => void;
}

const PostDetailsCardContextDefaultValue: PostDetailsCardContextPayload = {
    soundControlContainer: null,
    isProductExpanded: false,
    activeIndex: 0,
    sizes: { width: 0, height: 0 },
    setSoundControlContainer: noop,
    setIsProductExpanded: noop,
    setActiveIndex: noop,
    setSizes: noop,
};

export const PostDetailsCardContext = React.createContext<PostDetailsCardContextPayload>(
    PostDetailsCardContextDefaultValue,
);

export const usePostDetailsCardContext = () => useContext(PostDetailsCardContext);

export type PostDetailsCardProps = PostCardProps & { user?: UserUI };

export const PostDetailsCard = withInvariant(
    (props: PostDetailsCardProps) => {
        const { onVisibilityChange, index, post } = props;

        const [soundControlContainer, setSoundControlContainer] = useState<HTMLDivElement | null>(
            null,
        );
        const [isProductExpanded, setIsProductExpanded] = useState<boolean>(false);
        const [activeIndex, setActiveIndex] = useState<number>(0);
        const [sizes, setSizes] = useState(getCarouselSizes(post));

        const postRef = useRef<HTMLDivElement>(null);
        useTrackPostVisible({ onVisibilityChange, index, activeIndex, post, postRef });

        const { isMobile } = getIsMobile();

        const contextValue: PostDetailsCardContextPayload = {
            soundControlContainer,
            isProductExpanded,
            activeIndex,
            sizes,
            setSoundControlContainer,
            setIsProductExpanded,
            setActiveIndex,
            setSizes,
        };

        return (
            <Root ref={postRef} isMobile={isMobile}>
                <PostDetailsCardContext.Provider value={contextValue}>
                    <Box
                        height={sizes.height}
                        width={sizes.width}
                        maxWidth={{ sm: '100%' }}
                        alignSelf="center"
                        position="relative"
                    >
                        <PostHeader {...props} />
                        <PostCarousel {...props} />
                        {isMobile ? <PostControls {...props} /> : null}
                        {isMobile ? <PostFooter {...props} /> : null}
                    </Box>
                    <PostText {...props} />
                </PostDetailsCardContext.Provider>
            </Root>
        );
    },
    () => null,
);
